/**
 * `/auth/return` — the landing route after centralized login. The refresh
 * cookie is now set, so we run one silent refresh to mint the in-memory bearer,
 * then navigate to the stashed deep link (or home). On failure we send the user
 * to the auth-error page.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bootRefresh, popDeepLink } from '@/auth/silentRefresh';
import { getAccessToken } from '@/auth/authStore';
import { useAuth } from '@/auth/authHooks';
import { HOME_PATH } from '@/routes/home';

export function AuthReturn() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const ok = await bootRefresh();
      if (ok) {
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        const deep = popDeepLink();
        navigate(deep ?? HOME_PATH, { replace: true });
      } else {
        setFailed(true);
        navigate('/auth/error', { replace: true });
      }
    })();
  }, [navigate, setAuthed]);

  return (
    <main role="status" aria-live="polite" style={{ padding: '2rem', textAlign: 'center' }}>
      <p>{failed ? 'Sign-in failed. Redirecting…' : 'Completing sign-in…'}</p>
    </main>
  );
}
